import React, {
    createContext,
    useContext,
    useState,
    useRef,
    useCallback,
    useMemo,
    useEffect,
    forwardRef,
    useLayoutEffect,
    ElementType,
    ComponentPropsWithoutRef,
    ComponentPropsWithRef,
    CSSProperties,
    ReactNode,
} from "react"
import { createPortal } from "react-dom"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"

export type DropdownPosition =
    | "bottom-left"
    | "bottom-right"
    | "bottom-center"
    | "top-left"
    | "top-right"
    | "top-center"

type AsProp<C extends ElementType> = {
    as?: C
}

type PropsToOmit<C extends ElementType, P> = keyof (AsProp<C> & P)

type PolymorphicComponentProp<C extends ElementType, P = unknown> = React.PropsWithChildren<
    P & AsProp<C>
> &
    Omit<ComponentPropsWithoutRef<C>, PropsToOmit<C, P>>

type PolymorphicRef<C extends ElementType> = ComponentPropsWithRef<C>["ref"]

type PolymorphicComponent<Default extends ElementType, P = unknown> = <
    C extends ElementType = Default,
>(
    props: PolymorphicComponentProp<C, P> & { ref?: PolymorphicRef<C> }
) => React.ReactElement | null

interface DropdownContextType {
    isOpen: boolean
    toggle: () => void
    close: () => void
    triggerRef: React.RefObject<HTMLElement | null>
    menuRef: React.RefObject<HTMLElement | null>
    disabled: boolean
    stopPropagation: boolean
    registerNestedMenu: (menu: HTMLElement) => () => void
    registerWithAncestor: (menu: HTMLElement) => () => void
}

const DropdownContext = createContext<DropdownContextType | null>(null)

const useDropdown = () => {
    const ctx = useContext(DropdownContext)
    if (!ctx) throw new Error("useDropdown must be used within <Dropdown>")
    return ctx
}

const useParentDropdown = () => useContext(DropdownContext)

const getPositionStyles = (
    trigger: HTMLElement,
    menu: HTMLElement,
    position: DropdownPosition,
    replaceTrigger: boolean,
    matchTriggerWidth?: boolean
): CSSProperties => {
    const tRect = trigger.getBoundingClientRect()
    const mRect = menu.getBoundingClientRect()

    let top = tRect.bottom + window.scrollY + 4
    let left = tRect.left + window.scrollX

    switch (position) {
        case "bottom-right":
            left = tRect.right + window.scrollX - mRect.width
            break
        case "bottom-center":
            left = tRect.left + window.scrollX + (tRect.width - mRect.width) / 2
            break
        case "top-left":
            top = tRect.top + window.scrollY - mRect.height - 4
            break
        case "top-right":
            top = tRect.top + window.scrollY - mRect.height - 4
            left = tRect.right + window.scrollX - mRect.width
            break
        case "top-center":
            top = tRect.top + window.scrollY - mRect.height - 4
            left = tRect.left + window.scrollX + (tRect.width - mRect.width) / 2
            break
    }

    if (replaceTrigger) {
        switch (position) {
            case "bottom-left":
            case "bottom-right":
            case "bottom-center":
                top = tRect.top + window.scrollY
                break
            case "top-left":
            case "top-right":
            case "top-center":
                top = tRect.bottom + window.scrollY - mRect.height
                break
        }
    }

    // When matching trigger width, use whichever is larger for clamping
    const effectiveWidth = matchTriggerWidth ? Math.max(tRect.width, mRect.width) : mRect.width

    // Clamp horizontally
    if (left + effectiveWidth > window.scrollX + window.innerWidth) {
        left = window.scrollX + window.innerWidth - effectiveWidth
    }
    if (left < window.scrollX) {
        left = window.scrollX
    }

    // Clamp vertically
    if (top + mRect.height > window.scrollY + window.innerHeight) {
        top = window.scrollY + window.innerHeight - mRect.height - 8
    }
    if (top < window.scrollY) {
        top = window.scrollY + 8
    }

    const styles: CSSProperties = { top, left, position: "absolute" }
    if (matchTriggerWidth) {
        styles.minWidth = tRect.width
    }
    return styles
}

interface DropdownRootProps {
    children: ReactNode
    onClose?: () => void
    onOpenChange?: (isOpen: boolean) => void
    disabled?: boolean
    stopPropagation?: boolean
    defaultOpen?: boolean
    open?: boolean
}

const Root = ({
    children,
    onClose,
    onOpenChange,
    disabled = false,
    stopPropagation = false,
    defaultOpen = false,
    open: controlledOpen,
}: DropdownRootProps) => {
    const isControlled = controlledOpen !== undefined
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
    const isOpen = isControlled ? controlledOpen : uncontrolledOpen
    const triggerRef = useRef<HTMLElement | null>(null)
    const menuRef = useRef<HTMLElement | null>(null)
    const nestedMenus = useRef(new Set<HTMLElement>())
    const parent = useParentDropdown()

    const registerNestedMenu = useCallback((menu: HTMLElement) => {
        nestedMenus.current.add(menu)
        return () => {
            nestedMenus.current.delete(menu)
        }
    }, [])

    // Using a ref for the callback prevents unnecessary re-renders
    // if the consumer passes an inline function for onClose
    const onCloseRef = useRef(onClose)
    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    const onOpenChangeRef = useRef(onOpenChange)
    useEffect(() => {
        onOpenChangeRef.current = onOpenChange
    }, [onOpenChange])

    const close = useCallback(() => {
        if (isControlled) {
            onOpenChangeRef.current?.(false)
        } else {
            setUncontrolledOpen(false)
        }
    }, [isControlled])

    const toggle = useCallback(() => {
        if (isControlled) {
            onOpenChangeRef.current?.(!controlledOpen)
        } else {
            setUncontrolledOpen((prev) => !prev)
        }
    }, [isControlled, controlledOpen])

    const prevIsOpenRef = useRef(false)
    useEffect(() => {
        if (isOpen === prevIsOpenRef.current) return
        prevIsOpenRef.current = isOpen
        if (!isOpen) onCloseRef.current?.()
        onOpenChangeRef.current?.(isOpen)
    }, [isOpen])

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (isOpen && e.key === "Escape") close()
        }
        window.addEventListener("keydown", onKeyDown)
        return () => window.removeEventListener("keydown", onKeyDown)
    }, [isOpen, close])

    // Replaced external ClickAwayListener with native hook logic
    useEffect(() => {
        if (!isOpen) return
        const listener = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node
            if (
                triggerRef.current?.contains(target) ||
                menuRef.current?.contains(target) ||
                [...nestedMenus.current].some((menu) => menu.contains(target))
            ) {
                return
            }
            close()
        }
        document.addEventListener("mousedown", listener)
        document.addEventListener("touchstart", listener)
        return () => {
            document.removeEventListener("mousedown", listener)
            document.removeEventListener("touchstart", listener)
        }
    }, [isOpen, close])

    const registerWithAncestor = useCallback(
        (menu: HTMLElement) => parent?.registerNestedMenu(menu) ?? (() => {}),
        [parent]
    )

    const value = useMemo(
        () => ({
            isOpen,
            toggle,
            close,
            triggerRef,
            menuRef,
            disabled,
            stopPropagation,
            registerNestedMenu,
            registerWithAncestor,
        }),
        [isOpen, toggle, close, disabled, stopPropagation, registerNestedMenu, registerWithAncestor]
    )

    return <DropdownContext.Provider value={value}>{children}</DropdownContext.Provider>
}

const TriggerButton = forwardRef(
    <C extends ElementType = "button">(
        {
            as,
            children,
            className,
            withoutDropdownArrow = false,
            clickBehaviour = "toggle",
            ...props
        }: PolymorphicComponentProp<
            C,
            {
                withoutDropdownArrow?: boolean
                clickBehaviour?: "toggle" | "open"
            }
        >,
        ref: React.ForwardedRef<HTMLElement>
    ) => {
        const { toggle, triggerRef, isOpen, disabled, stopPropagation } = useDropdown()
        const Component = as || "button"

        // Single source of truth for ref assignment
        const setRefs = useCallback(
            (node: HTMLElement | null) => {
                // Update context ref
                ;(triggerRef as React.MutableRefObject<HTMLElement | null>).current = node

                // Forward the ref to the consumer
                if (typeof ref === "function") {
                    ref(node)
                } else if (ref) {
                    ;(ref as React.MutableRefObject<HTMLElement | null>).current = node
                }
            },
            [ref, triggerRef]
        )

        return (
            <Component
                {...props}
                ref={setRefs}
                onClick={(e) => {
                    if (stopPropagation) {
                        e.stopPropagation()
                    }
                    if (disabled) return
                    if (clickBehaviour === "toggle" || !isOpen) toggle()
                }}
                aria-expanded={isOpen}
                data-state={isOpen ? "open" : "closed"}
                type="button"
                aria-haspopup="true"
                className={classNames(
                    "cursor-pointer border border-transparent hover:border-gray-500 rounded-lg flex gap-1 items-center px-1 py-1 text-white min-h-[32px] relative",
                    isOpen && "bg-gray-700 border-gray-500",
                    className
                )}
            >
                {children}
                {!withoutDropdownArrow && (
                    <Icons.ExtraSmallIcon
                        className="ml-auto !relative"
                        Icon={Icons.ArrowDropdown}
                    />
                )}
            </Component>
        )
    }
) as PolymorphicComponent<
    "button",
    {
        withoutDropdownArrow?: boolean
        clickBehaviour?: "toggle" | "open"
    }
>

const Trigger = forwardRef(
    <C extends ElementType = "button">(
        {
            as,
            children,
            className,
            clickBehaviour = "toggle",
            ...props
        }: PolymorphicComponentProp<C, { clickBehaviour?: "toggle" | "open" }>,
        ref: React.ForwardedRef<HTMLElement>
    ) => {
        const { toggle, triggerRef, isOpen, disabled, stopPropagation } = useDropdown()
        const Component = as || "button"

        const setRefs = useCallback(
            (node: HTMLElement | null) => {
                ;(triggerRef as React.MutableRefObject<HTMLElement | null>).current = node
                if (typeof ref === "function") ref(node)
                else if (ref) ref.current = node
            },
            [ref, triggerRef]
        )

        return (
            <Component
                {...props}
                ref={setRefs}
                onClick={(e) => {
                    if (stopPropagation) {
                        e.stopPropagation()
                    }
                    if (disabled) return
                    if (clickBehaviour === "toggle" || !isOpen) toggle()
                }}
                type="button"
                aria-expanded={isOpen}
                data-state={isOpen ? "open" : "closed"}
                className={classNames("cursor-pointer flex items-center", className)}
            >
                {children}
            </Component>
        )
    }
) as PolymorphicComponent<"button", { clickBehaviour?: "toggle" | "open" }>

const Menu = forwardRef(
    <C extends ElementType = "div">(
        {
            as,
            children,
            className,
            position = "bottom-left",
            replaceTrigger = false,
            style,
            unstyled,
            matchTriggerWidth,
            ...props
        }: PolymorphicComponentProp<
            C,
            {
                position?: DropdownPosition
                replaceTrigger?: boolean
                unstyled?: boolean
                matchTriggerWidth?: boolean
            }
        >,
        ref: React.ForwardedRef<HTMLElement>
    ) => {
        const { isOpen, menuRef, triggerRef, registerWithAncestor } = useDropdown()
        const [coords, setCoords] = useState<CSSProperties>({})
        const Component = as || "div"

        useEffect(() => {
            if (!isOpen || !menuRef.current) return
            return registerWithAncestor(menuRef.current)
        }, [isOpen, menuRef, registerWithAncestor])

        useLayoutEffect(() => {
            if (!isOpen || !triggerRef.current || !menuRef.current) return
            const update = () =>
                setCoords(
                    getPositionStyles(
                        triggerRef.current!,
                        menuRef.current!,
                        position,
                        replaceTrigger,
                        matchTriggerWidth
                    )
                )
            update()
            window.addEventListener("resize", update)
            document.addEventListener("scroll", update, { capture: true })
            return () => {
                window.removeEventListener("resize", update)
                document.removeEventListener("scroll", update, { capture: true })
            }
        }, [isOpen, position, triggerRef, menuRef, matchTriggerWidth, replaceTrigger])

        const setRefs = useCallback(
            (node: HTMLElement | null) => {
                ;(menuRef as React.MutableRefObject<HTMLElement | null>).current = node
                if (typeof ref === "function") ref(node)
                else if (ref) ref.current = node
            },
            [ref, menuRef]
        )

        if (!isOpen) return null

        return createPortal(
            <Component
                {...props}
                ref={setRefs}
                role="menu"
                style={{ ...coords, zIndex: 9999, ...style }}
                className={classNames(
                    "fixed",
                    !unstyled && "min-w-max rounded-md bg-gray-700 py-1 shadow-dp4",
                    className
                )}
            >
                {children}
            </Component>,
            document.getElementById("portal")!
        )
    }
) as PolymorphicComponent<
    "div",
    {
        position?: DropdownPosition
        replaceTrigger?: boolean
        unstyled?: boolean
        matchTriggerWidth?: boolean
    }
>

const Item = forwardRef(
    <C extends ElementType = "div">(
        {
            as,
            children,
            onClick,
            className,
            clickBehaviour = "close",
            ...props
        }: PolymorphicComponentProp<C, { clickBehaviour?: "close" | "keep-open" | "wait" }>,
        ref: React.ForwardedRef<HTMLElement>
    ) => {
        const [isWaiting, setIsWaiting] = useState(false)
        const { close, stopPropagation } = useDropdown()
        const Component = as || "div"

        const handleClick = async (e: React.MouseEvent | React.KeyboardEvent) => {
            if (stopPropagation) {
                e.stopPropagation()
            }
            if (isWaiting) return
            if (clickBehaviour === "wait") {
                setIsWaiting(true)
                try {
                    await onClick?.(e as React.MouseEvent)
                } catch (err) {
                    console.error(err)
                } finally {
                    setIsWaiting(false)
                }
            } else {
                onClick?.(e as React.MouseEvent)
            }
            if (clickBehaviour === "close" || clickBehaviour === "wait") close()
        }

        const handleRef = useCallback(
            (node: HTMLElement | null) => {
                if (typeof ref === "function") ref(node)
                else if (ref) ref.current = node
            },
            [ref]
        )

        return (
            <Component
                {...props}
                ref={handleRef}
                role="menuitem"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        handleClick(e)
                    }
                }}
                className={classNames("block w-full cursor-pointer", className)}
            >
                {children}
            </Component>
        )
    }
) as PolymorphicComponent<"div", { clickBehaviour?: "close" | "keep-open" | "wait" }>

const ButtonItem = forwardRef<
    HTMLButtonElement,
    ComponentPropsWithoutRef<"button"> & { clickBehaviour?: "close" | "keep-open" | "wait" }
>(({ children, onClick, className, clickBehaviour = "close", ...props }, ref) => {
    const [isWaiting, setIsWaiting] = useState(false)
    const { close, stopPropagation } = useDropdown()

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        if (stopPropagation) {
            e.stopPropagation()
        }
        if (isWaiting) return
        if (clickBehaviour === "wait") {
            setIsWaiting(true)
            try {
                await onClick?.(e)
            } catch (err) {
                console.error(err)
            } finally {
                setIsWaiting(false)
            }
        } else {
            onClick?.(e)
        }
        if (clickBehaviour === "close" || clickBehaviour === "wait") close()
    }

    return (
        <button
            ref={ref}
            type="button"
            role="menuitem"
            onClick={handleClick}
            tabIndex={0}
            className={classNames(
                "w-full hover:bg-gray-500 cursor-pointer text-left text-white min-h-[32px] h-full flex items-center px-3 py-1",
                className
            )}
            {...props}
        >
            {children}
        </button>
    )
})

ButtonItem.displayName = "Dropdown.ButtonItem"

const Close = forwardRef(
    <C extends ElementType = "button">(
        {
            as,
            children,
            onClick,
            closeBehaviour = "immediate",
            ...props
        }: PolymorphicComponentProp<C, { closeBehaviour?: "immediate" | "wait" }>,
        ref: React.ForwardedRef<HTMLElement>
    ) => {
        const [isWaiting, setIsWaiting] = useState(false)
        const { close, stopPropagation } = useDropdown()
        const Component = as || "button"

        const setRef = useCallback(
            (node: HTMLElement | null) => {
                if (typeof ref === "function") ref(node)
                else if (ref) ref.current = node
            },
            [ref]
        )

        const handleClick =
            closeBehaviour === "immediate"
                ? (e: React.MouseEvent) => {
                      if (stopPropagation) {
                          e.stopPropagation()
                      }
                      onClick?.(e)
                      close()
                  }
                : async (e: React.MouseEvent) => {
                      if (stopPropagation) {
                          e.stopPropagation()
                      }
                      if (isWaiting) return
                      setIsWaiting(true)
                      try {
                          await onClick?.(e)
                      } catch (err) {
                          console.error(err)
                      } finally {
                          setIsWaiting(false)
                      }
                      close()
                  }

        return (
            <Component {...props} ref={setRef} onClick={handleClick} disabled={isWaiting}>
                {children}
            </Component>
        )
    }
) as PolymorphicComponent<"button", { closeBehaviour?: "immediate" | "wait" }>

export const Dropdown = Object.assign(Root, {
    TriggerButton,
    Trigger,
    Menu,
    Item,
    ButtonItem,
    Close,
})
